import { v4 as uuidv4 } from 'uuid';

import type { AuthorDataRaw, AuthorData, SocialLink } from '../types';

const normalizeSocialLinks = (author: AuthorDataRaw): SocialLink[] => {
    const socialLinks: SocialLink[] = [
        {
            platform: 'Website',
            url: author?.websiteUrl || '',
            enabled: !!author?.websiteUrl,
            icon: 'website',
            id: uuidv4(),
        },
        {
            platform: 'Facebook',
            url: author?.facebookUrl || '',
            enabled: !!author?.facebookUrl,
            icon: 'facebook',
            id: uuidv4(),
        },
        {
            platform: 'Instagram',
            url: author?.instagramUrl || '',
            enabled: !!author?.instagramUrl,
            icon: 'instagram',
            id: uuidv4(),
        },
        {
            platform: 'LinkedIn',
            url: author?.linkedinUrl || '',
            enabled: !!author?.linkedinUrl,
            icon: 'linkedin',
            id: uuidv4(),
        },
        {
            platform: 'Pinterest',
            url: author?.pinterestUrl || '',
            enabled: !!author?.pinterestUrl,
            icon: 'pinterest',
            id: uuidv4(),
        },
        {
            platform: 'SoundCloud',
            url: author?.soundcloudUrl || '',
            enabled: !!author?.soundcloudUrl,
            icon: 'soundcloud',
            id: uuidv4(),
        },
        {
            platform: 'Tumblr',
            url: author?.tumblrUrl || '',
            enabled: !!author?.tumblrUrl,
            icon: 'tumblr',
            id: uuidv4(),
        },
        {
            platform: 'Wikipedia',
            url: author?.wikipediaUrl || '',
            enabled: !!author?.wikipediaUrl,
            icon: 'wikipedia',
            id: uuidv4(),
        },
        {
            platform: 'Twitter',
            url: author?.twitterUsername ? `https://twitter.com/${author.twitterUsername}` : '',
            enabled: !!author?.twitterUsername,
            icon: 'twitter',
            id: uuidv4(),
        },
        {
            platform: 'YouTube',
            url: author?.youtubeUrl || '',
            enabled: !!author?.youtubeUrl,
            icon: 'youtube',
            id: uuidv4(),
        },
        {
            platform: 'TikTok',
            url: '',
            enabled: false,
            icon: 'tiktok',
            id: uuidv4(),
        },
    ];

    return socialLinks.filter((link) => link.enabled);
};

export const normalizeAuthorData = (author: AuthorDataRaw): AuthorData => {
    return {
        firstName: author?.firstName ?? undefined,
        lastName: author?.lastName ?? undefined,
        name: author?.name ?? undefined,
        description: author?.description ?? undefined,
        slug: author?.slug ?? undefined,
        avatar: {
            url: author?.avatar?.url ?? undefined,
            width: author?.avatar?.width ?? undefined,
            height: author?.avatar?.height ?? undefined,
        },
        authorPhotograph: author?.extraAuthorMeta?.authorPhotograph
            ? {
                  altText: author.extraAuthorMeta.authorPhotograph.altText ?? undefined,
                  caption: author.extraAuthorMeta.authorPhotograph.caption ?? undefined,
                  fullURL: author.extraAuthorMeta.authorPhotograph.fullURL ?? undefined,
                  mediaDetails: author.extraAuthorMeta.authorPhotograph.mediaDetails ?? undefined,
              }
            : undefined,
        jobDescription: author?.extraAuthorMeta?.jobDescription ?? undefined,
        seo: author?.seo
            ? {
                  breadcrumbTitle: author.seo.breadcrumbTitle ?? undefined,
                  title: author.seo.title ?? undefined,
                  metaDesc: author.seo.metaDesc ?? undefined,
                  schema: author.seo.schema?.raw ?? undefined,
                  opengraph: {
                      ogTitle: author.seo.opengraphTitle ?? undefined,
                      ogDescription: author.seo.opengraphDescription ?? undefined,
                  },
                  fullHead: author.seo.fullHead ?? undefined,
              }
            : undefined,
        socialLinks: normalizeSocialLinks(author),
    };
};

export default normalizeAuthorData;
